// Globales Overlay für Serien-Job-Fortschritt + kleiner Verlauf (08/2026 Phase 2).
// - Fortschrittskarten für laufende Buchungs-/Lösch-Jobs (überstehen Reload).
// - "Verlauf"-Button (unten links) zeigt die letzten Hintergrund-Jobs mit Ergebnis.
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { History, X, CalendarCheck, Trash2, CheckCircle2, AlertTriangle, Loader2, Undo2 } from 'lucide-react';
import { SeriesSyncProgress } from '@/components/appointments/SeriesSyncProgress';
import { subscribeSeriesJobs, addSeriesJob, removeSeriesJob } from '@/lib/seriesJobsStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Kunden-/Public-Routen: hier ist SeriesJobsOverlay (Admin-Werkzeug) TABU
// (verhindert Leak fremder Buchungsaktivität ins Kundenportal, wenn ein
// Admin-Token im selben Browser liegt).
const NON_ADMIN_PREFIXES = [
  '/portal', '/app', '/app-redirect', '/mobile', '/login', '/register',
  '/buchen', '/kursplan', '/schedule', '/preisliste', '/pricing',
  '/interesse', '/interesse-mini', '/spontan-buchen', '/waitlist-confirm',
  '/waitlist/quick-confirm', '/widget', '/agb', '/datenschutz',
  '/anamnese', '/umbuchen',
];

export default function SeriesJobsOverlay() {
  const [ids, setIds] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname || '';
  const isNonAdminRoute = NON_ADMIN_PREFIXES.some(
    (p) => path === p || path.startsWith(p + '/')
  );

  const jumpToJob = (j) => {
    if (!j?.first_start) return;
    setShowHistory(false);
    try { navigate('/appointments'); } catch (_) {}
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('jump-to-calendar-date', { detail: { date: j.first_start } }));
    }, 200);
  };

  useEffect(() => subscribeSeriesJobs(setIds), []);

  useEffect(() => {
    let cancelled = false;
    const seed = async () => {
      if (isNonAdminRoute) return;
      if (!localStorage.getItem('token')) return;
      try {
        const res = await axios.get(`${API}/appointments/series/running-jobs`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (cancelled) return;
        (res.data?.jobs || []).forEach((j) => j.series_id && addSeriesJob(j.series_id));
      } catch (_) { /* noop */ }
    };
    seed();
    return () => { cancelled = true; };
  }, []);

  const handleDone = useCallback((seriesId) => {
    removeSeriesJob(seriesId);
    window.dispatchEvent(new CustomEvent('series-job-done', { detail: { seriesId } }));
  }, []);

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/appointments/series/recent-jobs`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setHistory(res.data?.jobs || []);
    } catch (_) { setHistory([]); }
  };

  const openHistory = () => { setShowHistory(true); loadHistory(); };

  // Fester "Verlauf"-Eintrag im Kalender-Menü öffnet das Panel über ein Event.
  useEffect(() => {
    const onOpen = () => openHistory();
    window.addEventListener('open-series-history', onOpen);
    return () => window.removeEventListener('open-series-history', onOpen);
  }, []);

  const [undoing, setUndoing] = useState(null);
  const undoDeletion = async (jobId) => {
    setUndoing(jobId);
    try {
      const res = await axios.post(`${API}/appointments/series/undo/${jobId}`);
      const n = res.data?.restored || 0;
      const { toast } = await import('sonner');
      if (n > 0) toast.success(`${n} Termine wiederhergestellt ✅`);
      else toast.info('Nichts wiederherzustellen (evtl. schon erledigt).');
      await loadHistory();
      window.dispatchEvent(new CustomEvent('series-job-done', { detail: { seriesId: res.data?.series_id } }));
    } catch (e) {
      const { toast } = await import('sonner');
      toast.error(e?.response?.data?.detail || 'Rückgängig fehlgeschlagen');
    } finally {
      setUndoing(null);
    }
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch (_) { return ''; }
  };

  const hasToken = !!(typeof localStorage !== 'undefined' && localStorage.getItem('token'));

  // Nie im Kundenportal / auf Public-Seiten anzeigen (Datenschutz: fremde Buchungen).
  if (isNonAdminRoute) return null;

  return (
    <>
      {hasToken && ids.map((sid, i) => (
        <SeriesSyncProgress key={sid} seriesId={sid} offset={i + (ids.length ? 0 : 0)} onDone={handleDone} />
      ))}

      {/* Verlauf-Button (nur eingeloggt, und wenn keine aktive Karte den Platz belegt) */}
      {hasToken && ids.length === 0 && (
        <button
          data-testid="series-history-btn"
          onClick={openHistory}
          title="Hintergrund-Verlauf"
          className="fixed bottom-6 left-6 z-40 flex items-center gap-1.5 rounded-full bg-white border border-gray-200 shadow-md text-gray-600 text-xs font-medium px-3 py-1.5 hover:bg-gray-50"
        >
          <History className="w-3.5 h-3.5" /> Verlauf
        </button>
      )}

      {showHistory && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center sm:justify-center bg-black/40" onClick={() => setShowHistory(false)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:w-[420px] max-h-[70vh] overflow-y-auto p-4 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="series-history-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-800 flex items-center gap-2"><History className="w-4 h-4" /> Hintergrund-Verlauf</h3>
              <button onClick={() => setShowHistory(false)} className="p-1 rounded hover:bg-gray-100 text-gray-400"><X className="w-4 h-4" /></button>
            </div>
            {history.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">Noch keine Hintergrund-Aktionen.</p>
            ) : (
              <ul className="space-y-2">
                {history.map((j) => {
                  const del = j.kind === 'deletion';
                  const KindIcon = del ? Trash2 : CalendarCheck;
                  const running = j.status === 'running';
                  const error = j.status === 'error';
                  const StatusIcon = running ? Loader2 : error ? AlertTriangle : CheckCircle2;
                  const statusColor = running ? 'text-indigo-500' : error ? 'text-red-500' : 'text-emerald-500';
                  return (
                    <li key={j.id} className="flex items-center gap-3 text-sm border border-gray-100 rounded-lg px-3 py-2" data-testid={`history-job-${j.id}`}>
                      <KindIcon className={`w-4 h-4 ${del ? 'text-rose-600' : 'text-indigo-600'} flex-shrink-0`} />
                      <div
                        className={`flex-1 min-w-0 ${j.first_start ? 'cursor-pointer' : ''}`}
                        onClick={() => jumpToJob(j)}
                        title={j.first_start ? 'Im Kalender anzeigen' : ''}
                        data-testid={`history-jump-${j.id}`}
                      >
                        <p className={`text-gray-800 truncate ${j.first_start ? 'hover:text-indigo-700 hover:underline' : ''}`}>
                          {(j.created_count ?? j.processed ?? 0)} {del ? 'gelöscht' : 'gebucht'}
                          {j.customer_name ? ` · ${j.customer_name}` : ''}
                          {j.series_label ? ` · ${j.series_label}` : ''}
                        </p>
                        <p className="text-gray-400 text-xs">
                          {fmt(j.started_at)}
                          {j.skipped_count > 0 && ` · ${j.skipped_count} übersprungen`}
                          {j.undone && ' · wiederhergestellt'}
                        </p>
                        {(j.created_count === 0 && j.skipped_reason) && (
                          <p className="text-amber-700 text-xs mt-0.5 flex items-start gap-1" data-testid={`history-reason-${j.id}`}>
                            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span>{j.skipped_reason}</span>
                          </p>
                        )}
                      </div>
                      {del && !running && !error && !j.undone && (j.affected_ids?.length || j.created_count) ? (
                        <button
                          data-testid={`history-undo-${j.id}`}
                          onClick={() => undoDeletion(j.id)}
                          disabled={undoing === j.id}
                          className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 border border-indigo-200 rounded-md px-2 py-1 flex-shrink-0 disabled:opacity-50"
                        >
                          <Undo2 className="w-3.5 h-3.5" /> {undoing === j.id ? '…' : 'Rückgängig'}
                        </button>
                      ) : (
                        <StatusIcon className={`w-4 h-4 ${statusColor} ${running ? 'animate-spin' : ''} flex-shrink-0`} />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}
